var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import invariant from 'invariant';
import Loader from '../Loader';
import { DefaultTheme } from '../../Themes';
import { Icon } from '../Icon';
import { ButtonSize, ButtonType, HtmlButtonType, ButtonIconLocation, } from './types';
const StyledBaseButton = styled.button `
  display: inline-flex;
  flex-direction: ${({ iconLocation }) => iconLocation === ButtonIconLocation.LEFT ? 'row' : 'row-reverse'};
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding: 0 1.333333333rem;
  border-radius: 3px;
  font-family: inherit;
  font-weight: 600;
  line-height: 1;
  opacity: ${({ disabled }) => disabled ? 0.3 : 1};

  ${({ size }) => size === ButtonSize.SMALL && css `
    min-width: 9.333333333rem;
    min-height: 2.666666667rem;
    font-size: 1.166666667rem;
  `}

  ${({ size }) => size === ButtonSize.MEDIUM && css `
    min-width: 9.666666667rem;
    min-height: 3.166666667rem;
    font-size: 1.166666667rem;
  `}

  ${({ size }) => size === ButtonSize.LARGE && css `
    min-width: 10rem;
    min-height: 3.666666667rem;
    font-size: 1.333333333rem;
  `}

  &::first-letter {
    text-transform: capitalize;
  }

  &:hover {
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  }

  &:active {
    opacity: 0.8;
  }
`;
const StyledFilledButton = styled(StyledBaseButton) `
  border: 0;
`;
const StyledOutlinedButton = styled(StyledBaseButton) `
  border: 1px solid;
`;
const StyledPrimaryButton = styled(StyledFilledButton) `
  background-color: ${({ theme }) => theme.ColorNames.MARINER.hex};
  color: ${({ theme }) => theme.ThemeColors.WHITE.hex};
`;
const StyledSecondaryButton = styled(StyledFilledButton) `
  background-color: ${({ theme }) => theme.ColorNames.THUNDER.hex};
  color: ${({ theme }) => theme.ThemeColors.WHITE.hex};
`;
const StyledTertiaryButton = styled(StyledOutlinedButton) `
  background-color: ${({ theme }) => theme.ThemeColors.WHITE.hex};
  border-color: ${({ theme }) => theme.ColorNames.MARINER.hex};
  color: ${({ theme }) => theme.ColorNames.MARINER.hex};
`;
const StyledDangerButton = styled(StyledFilledButton) `
  background-color: ${({ theme }) => theme.ColorNames.MONZA.hex};
  color: ${({ theme }) => theme.ThemeColors.WHITE.hex};
`;
const StyledWarningButton = styled(StyledFilledButton) `
  background-color: ${({ theme }) => theme.ColorNames.BUTTERCUP.hex};
  color: ${({ theme }) => theme.ThemeColors.WHITE.hex};
`;
const StyledInfoButton = styled(StyledFilledButton) `
  background-color: ${({ theme }) => theme.ColorNames.ATHENS_GRAY.hex};
  color: ${({ theme }) => theme.ColorNames.THUNDER.hex};
`;
const StyledNotFilledButton = styled(StyledBaseButton) `
  background-color: transparent;
  border: 0;
  color: ${({ theme }) => theme.ColorNames.THUNDER.hex};

  &:hover {
    background-color: ${({ theme }) => theme.ColorNames.ATHENS_GRAY.X800};
  }
`;
const StyledIcon = styled(Icon) `
  margin: 0 0.416666667rem;
`;
const StyledLoader = styled(Loader) `
  color: inherit;
`;
const BUTTON_MAPPER = {
    [ButtonType.PRIMARY]: StyledPrimaryButton,
    [ButtonType.SECONDARY]: StyledSecondaryButton,
    [ButtonType.TERTIARY]: StyledTertiaryButton,
    [ButtonType.DANGER]: StyledDangerButton,
    [ButtonType.WARNING]: StyledWarningButton,
    [ButtonType.INFO]: StyledInfoButton,
    [ButtonType.NOT_FILLED]: StyledNotFilledButton,
};
const Button = (_a) => {
    var { className, iconClassName, htmlButtonType = HtmlButtonType.BUTTON, icon, iconLocation = ButtonIconLocation.LEFT, isDisabled, isLoading, label, onClick, size = ButtonSize.MEDIUM, theme = DefaultTheme, type = ButtonType.PRIMARY, children = null } = _a, rest = __rest(_a, ["className", "iconClassName", "htmlButtonType", "icon", "iconLocation", "isDisabled", "isLoading", "label", "onClick", "size", "theme", "type", "children"]);
    invariant(Object.values(ButtonType).includes(type), 'Invalid button type provided.');
    const ButtonComponent = BUTTON_MAPPER[type];
    const handleClick = useCallback((event) => {
        if (!isDisabled && onClick) {
            onClick(event);
        }
    }, [isDisabled, onClick]);
    return (React.createElement(ThemeProvider, { theme: theme },
        React.createElement(ButtonComponent, Object.assign({ className: className, disabled: isDisabled, iconLocation: iconLocation, onClick: handleClick, size: size, type: htmlButtonType }, rest),
            isLoading
                ? React.createElement(StyledLoader, { name: 'circle' })
                : React.createElement(React.Fragment, null,
                    icon && React.createElement(StyledIcon, { className: iconClassName, icon: icon }),
                    label),
            children)));
};
export default Button;
